import { ChevronLeft, ChevronRight } from '../../../icons';
import { getNumberLocale } from '../../../lib/base-i18n';
import './Pagination.css';

/**
 * Pagination — 3형 (디자인 정본 E4).
 *
 * ★★★ **어느 형을 쓸지는 «서버가 무엇을 주느냐»가 정한다.** 취향이 아니다.
 *    · `offset` — 서버가 **총 건수를 준다** → 쪽 번호를 그린다
 *    · `cursor` — `nextCursor` 만 준다 → **총 건수를 적지 않는다**
 *    · `more`   — 무한 목록 → 「더 보기」
 *
 * ★★★ **모르는 것을 아는 척하지 않는다.** 커서 페이징에서 「총 428건」을 적으려면 그 숫자를
 *    어디선가 지어내야 한다. 그래서 `cursor` 형에는 **`total` 을 줄 수 있는 자리가 아예 없다** —
 *    타입이 막는다. 화면이 거짓말을 하지 않게 하는 가장 싼 방법이다.
 *
 * ★ 지금 앱에는 `WorkspacePagination` 하나뿐이고 메일은 `MailPager` 로 따로다.
 *   나머지 표에는 페이저가 아예 없다.
 */
interface Common {
  className?: string;
  /** 화면 낭독기용 이름. 한 화면에 페이저가 둘이면 구별돼야 한다. */
  ariaLabel?: string;
}

export interface OffsetPaginationProps extends Common {
  mode: 'offset';
  /** 1부터 센다. */
  page: number;
  pageSize: number;
  /** 총 건수 — **서버가 줄 때만** 이 형을 쓴다. */
  total: number;
  onPageChange: (page: number) => void;
  /** 좌측에 「총 N건 · M개씩」을 적는다. */
  showSummary?: boolean;
}

export interface CursorPaginationProps extends Common {
  mode: 'cursor';
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

export interface MorePaginationProps extends Common {
  mode: 'more';
  loading?: boolean;
  onMore: () => void;
  /** 남은 건수 — **아는 경우에만.** 모르면 주지 않는다(「더 보기」만 뜬다). */
  remaining?: number;
}

export type PaginationProps = OffsetPaginationProps | CursorPaginationProps | MorePaginationProps;

/** 1 2 3 … 9 — 양 끝과 지금 쪽 둘레만 남긴다. */
export function pageWindow(page: number, pageCount: number, span = 1): Array<number | 'gap'> {
  if (pageCount <= 1) return pageCount === 1 ? [1] : [];
  const keep = new Set<number>([1, pageCount]);
  for (let p = page - span; p <= page + span; p++) if (p >= 1 && p <= pageCount) keep.add(p);
  const sorted = [...keep].sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push('gap');
    out.push(p);
    prev = p;
  }
  return out;
}

export function Pagination(props: PaginationProps) {
  const cls = ['wc-pager', props.className ?? ''].filter(Boolean).join(' ');
  const nav = (children: React.ReactNode) => (
    <nav className={cls} aria-label={props.ariaLabel ?? '쪽 넘기기'}>{children}</nav>
  );
  const n = (v: number) => v.toLocaleString(getNumberLocale());

  if (props.mode === 'cursor') {
    const { hasPrev, hasNext, onPrev, onNext } = props;
    return nav(
      <div className="wc-pager__cursor">
        <button type="button" className="wc-pager__btn" disabled={!hasPrev} onClick={onPrev}>
          <ChevronLeft aria-hidden /> 이전
        </button>
        <button type="button" className="wc-pager__btn" disabled={!hasNext} onClick={onNext}>
          다음 <ChevronRight aria-hidden />
        </button>
      </div>,
    );
  }

  if (props.mode === 'more') {
    const { loading, onMore, remaining } = props;
    return nav(
      <button type="button" className="wc-pager__more" onClick={onMore} disabled={loading}>
        {loading ? '불러오는 중…' : '더 보기'}
        {/* 남은 수는 «아는 경우에만» 적는다 — 모르면 「더 보기」만 남는다. */}
        {!loading && remaining != null && <span className="wc-pager__rest">남은 {n(remaining)}</span>}
      </button>,
    );
  }

  const { page, pageSize, total, onPageChange, showSummary = true } = props;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const items = pageWindow(page, pageCount);

  return nav(
    <>
      {showSummary && (
        <span className="wc-pager__summary">총 {n(total)}건 · {n(pageSize)}개씩</span>
      )}
      <div className="wc-pager__pages">
        <button type="button" className="wc-pager__btn wc-pager__btn--icon" aria-label="이전 쪽"
          disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft aria-hidden />
        </button>
        {items.map((it, i) =>
          it === 'gap' ? (
            <span key={`gap-${i}`} className="wc-pager__gap" aria-hidden>…</span>
          ) : (
            <button
              key={it}
              type="button"
              className={`wc-pager__page${it === page ? ' is-on' : ''}`}
              aria-current={it === page ? 'page' : undefined}
              aria-label={`${it}쪽`}
              onClick={() => onPageChange(it)}
            >
              {it}
            </button>
          ),
        )}
        <button type="button" className="wc-pager__btn wc-pager__btn--icon" aria-label="다음 쪽"
          disabled={page >= pageCount} onClick={() => onPageChange(page + 1)}>
          <ChevronRight aria-hidden />
        </button>
      </div>
    </>,
  );
}
